/**
 * RealtimeSignalingService - Bridges the Realtime API message format
 * to/from the PeerCoordinator's ISignalingService interface.
 *
 * Outbound: PeerCoordinator calls sendOffer/sendAnswer/sendIceCandidate
 *           -> this service formats and sends via the realtime websocket.
 *
 * Inbound:  peer.ts handler calls handleIncomingMessage(PeerMessage)
 *           -> this service converts to SignalingMessage and notifies PeerCoordinator.
 */

import { PeerMessage, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import type { ISignalingService, SignalingMessage, PeerIdAssignedData } from '../../coordinators/peer/types';
import { sendMessage } from '../../api/modules/realtime';

export class RealtimeSignalingService implements ISignalingService {
  private signalingHandlers: Array<(msg: SignalingMessage) => void> = [];
  private peerIdAssignedHandlers: Array<(data: PeerIdAssignedData) => void> = [];

  sendOffer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit): void {
    Sentry.logger.info(`[RealtimeSignaling] Sending SDP offer from=${peerId} to=${targetPeerId}`);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_SDP_OFFER, {
        fromId: peerId,
        toId: targetPeerId,
        sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' },
      }),
    );
  }

  sendAnswer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit): void {
    Sentry.logger.info(`[RealtimeSignaling] Sending SDP answer from=${peerId} to=${targetPeerId}`);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_SDP_ANSWER, {
        fromId: peerId,
        toId: targetPeerId,
        sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' },
      }),
    );
  }

  sendIceCandidate(peerId: string, targetPeerId: string, candidates: RTCIceCandidateInit[]): void {
    Sentry.logger.info(`[RealtimeSignaling] Sending ICE candidates from=${peerId} to=${targetPeerId} count=${candidates.length}`);
    const json = candidates.map((c) => JSON.stringify(c));
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, {
        fromId: peerId,
        toId: targetPeerId,
        candidates: { json },
      }),
    );
  }

  requestPeerId(_type: string, _metadata: PeerMetadata): void {
    // Not needed: SessionCoordinator handles peer ID requests via the legacy path.
  }

  onPeerIdAssigned(handler: (data: PeerIdAssignedData) => void): () => void {
    this.peerIdAssignedHandlers.push(handler);
    return () => {
      const idx = this.peerIdAssignedHandlers.indexOf(handler);
      if (idx > -1) this.peerIdAssignedHandlers.splice(idx, 1);
    };
  }

  onSignalingMessage(handler: (msg: SignalingMessage) => void): () => void {
    this.signalingHandlers.push(handler);
    return () => {
      const idx = this.signalingHandlers.indexOf(handler);
      if (idx > -1) this.signalingHandlers.splice(idx, 1);
    };
  }

  /**
   * Called by the peer.ts handler to route incoming PeerMessages
   * into the PeerCoordinator via the registered signaling/peerIdAssigned handlers.
   */
  handleIncomingMessage(message: PeerMessage): void {
    if (message.type === PeerMessageType.PEER_GET_ID_ANSWER) {
      const data: PeerIdAssignedData = {
        peerId: message.peerId,
        peerIdType: message.peerIdType,
        iceServers: message.iceServers ? [message.iceServers] : [],
      };
      for (const handler of this.peerIdAssignedHandlers) {
        handler(data);
      }
      return;
    }

    const signalingMessage = this.convertToSignalingMessage(message);
    if (!signalingMessage) {
      Sentry.logger.warn(`[RealtimeSignaling] Could not convert message type=${message.type} to signaling message`);
      return;
    }

    Sentry.logger.info(
      `[RealtimeSignaling] Dispatching ${signalingMessage.type} from=${signalingMessage.fromId} to=${signalingMessage.toId} handlers=${this.signalingHandlers.length}`,
    );
    for (const handler of this.signalingHandlers) {
      handler(signalingMessage);
    }
  }

  private convertToSignalingMessage(message: PeerMessage): SignalingMessage | null {
    switch (message.type) {
      case PeerMessageType.PEER_SDP_OFFER:
        return {
          type: 'offer',
          fromId: message.fromId,
          toId: message.toId,
          sdp: message.sdp,
          peerMetadata: message.peerMetadata,
          peerIdType: message.peerIdType,
        };

      case PeerMessageType.PEER_SDP_ANSWER:
        return {
          type: 'answer',
          fromId: message.fromId,
          toId: message.toId,
          sdp: message.sdp,
        };

      case PeerMessageType.PEER_ICE_CANDIDATE: {
        const jsonCandidates = message.candidates.json;
        const candidates: RTCIceCandidateInit[] = [];
        for (const candidateJson of jsonCandidates) {
          try {
            candidates.push(JSON.parse(candidateJson) as RTCIceCandidateInit);
          } catch {
            Sentry.logger.warn('[RealtimeSignaling] Failed to parse ICE candidate');
          }
        }
        return {
          type: 'ice-candidate',
          fromId: message.fromId,
          toId: message.toId,
          candidates,
        };
      }

      default:
        return null;
    }
  }
}
